import { useState } from "react";
import { Instagram, Music2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function NewsletterInline({ testidPrefix = "footer" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-newsletter-form`} className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        data-testid={`${testidPrefix}-newsletter-input`}
        className="flex-1 rounded-full bg-ivory border border-champagne/40 px-5 py-3 font-body text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-bronze"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-newsletter-submit`}
        className="rounded-full bg-bronze px-7 py-3 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors disabled:opacity-60"
      >
        {loading ? "Joining…" : "Join The Fab Lab"}
      </button>
    </form>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative mt-10 border-t border-champagne/25 bg-ivory">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <p className="font-heading text-3xl tracking-[0.2em] text-ink">LEAH THAMES</p>
          <p className="mt-3 label-eyebrow">Fast. Feminine. Focused.</p>
          <div className="mt-6 flex items-center gap-4">
            <a href="https://instagram.com/leleskye_" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-2 text-ink/70 hover:text-bronze transition-colors">
              <Instagram size={18} /> <span className="font-body text-sm">@leleskye_</span>
            </a>
          </div>
          <div className="mt-2">
            <a href="https://tiktok.com/@leleskye" target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" className="flex items-center gap-2 text-ink/70 hover:text-bronze transition-colors">
              <Music2 size={18} /> <span className="font-body text-sm">@leleskye</span>
            </a>
          </div>
        </div>

        <div>
          <p className="label-eyebrow">Represented by</p>
          <p className="mt-3 font-heading text-xl text-ink">Blue Birds Sports</p>
          <div className="mt-4 space-y-1 font-body text-sm text-ink/70">
            <p>partnerships@bluebirdssports.com</p>
            <p>media@bluebirdssports.com</p>
          </div>
        </div>

        <div>
          <p className="label-eyebrow">The Fab Lab</p>
          <p className="mt-3 font-body text-sm text-ink/70 leading-relaxed">
            Track tips, healthy recipes, blog updates, Q&amp;A and behind-the-scenes. Join Leah's growing community.
          </p>
          <div className="mt-5">
            <NewsletterInline testidPrefix="footer" />
          </div>
        </div>
      </div>

      <div className="border-t border-champagne/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-ink/50">© {new Date().getFullYear()} Leah Thames. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="/admin/login" data-testid="footer-admin-link" className="font-body text-xs uppercase tracking-wide text-ink/45 hover:text-bronze transition-colors">Admin Portal</a>
            <p className="font-accent italic text-bronze">…and a little bit of pink.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
